/**
 * PretextChart - High-performance Canvas chart with dynamic responsive starburst
 * Features:
 * - 3000+ particles that avoid cursor (fish school effect)
 * - Dynamic starburst that spreads on zoom
 * - Hover shows @handle + price prominently
 * - Super smooth zoom/pan with momentum
 */
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Theme } from '../../theme';

// ============================================================================
// Types
// ============================================================================

export interface ContestEntry {
  id: number;
  twitter_handle: string;
  display_name?: string;
  profile_image_url?: string;
  price_prediction: number;
  submitted_at: string;
  is_current_user?: boolean;
  tweet_text?: string;
}

interface PriceDataPoint {
  date: Date;
  close: number;
}

interface PretextChartProps {
  contestEntries: ContestEntry[];
  priceData?: PriceDataPoint[];
  currentPrice: number;
  currentUserHandle?: string;
  theme: Theme;
  width?: number;
  height?: number;
  onEntryHover?: (entry: ContestEntry | null) => void;
  onEntryClick?: (entry: ContestEntry) => void;
}

interface ViewState {
  zoom: number;
  panX: number;
  panY: number;
  targetZoom: number;
  targetPanX: number;
  targetPanY: number;
  velocityX: number;
  velocityY: number;
  isDragging: boolean;
  lastMouseX: number;
  lastMouseY: number;
}

interface Particle {
  x: number;       // Grid X position
  y: number;       // Grid Y position  
  baseX: number;   // Original X
  baseY: number;   // Original Y
  z: number;       // Height value (pin displacement)
  vz: number;      // Velocity in Z
  size: number;    // Base size
  color: string;
  alpha: number;
  row: number;     // Grid row index
  col: number;     // Grid column index
}

interface EntryPosition {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  baseY: number;
}

interface AnimationState {
  entryPositions: Map<number, EntryPosition>;
  particles: Particle[];
  time: number;
  hoveredEntry: number | null;
  view: ViewState;
  mouseX: number;
  mouseY: number;
  // Wave ripple effect
  waveTime: number;      // Time since wave started (0 = no wave)
  waveOriginX: number;   // Center of wave
  waveOriginY: number;
  // Text display
  displayText: string;
  textPixels: Set<string>; // "x,y" keys for pixels that should be raised
}

// ============================================================================
// Styled Components
// ============================================================================

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #0a0a0f;
  border-radius: 12px;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

const HoverCard = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  background: rgba(10, 10, 20, 0.95);
  border: 2px solid #f7931a;
  border-radius: 12px;
  padding: 16px 20px;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: translate(-50%, -100%) translateY(-20px);
  transition: opacity 0.15s ease;
  z-index: 100;
  min-width: 160px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 30px rgba(247, 147, 26, 0.2);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const HoverHandle = styled.div`
  color: #1DA1F2;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const HoverPrice = styled.div`
  font-size: 24px;
  font-weight: 800;
  background: linear-gradient(90deg, #f7931a, #ffd700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HoverMeta = styled.div`
  color: #666;
  font-size: 11px;
  margin-top: 8px;
`;

const PerformanceStats = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: #0f0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  z-index: 50;
  border: 1px solid #0f03;
`;

const TextInputOverlay = styled.div<{ $visible: boolean; $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translate(-50%, -50%);
  z-index: 200;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transition: opacity 0.2s ease;
`;

const TextInput = styled.input`
  background: rgba(10, 10, 20, 0.95);
  border: 2px solid #f7931a;
  border-radius: 8px;
  padding: 12px 16px;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  min-width: 200px;
  outline: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 40px rgba(247, 147, 26, 0.3);
  
  &::placeholder {
    color: #666;
  }
  
  &:focus {
    border-color: #ffd700;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 60px rgba(247, 147, 26, 0.5);
  }
`;

const TextInputButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(247, 147, 26, 0.15);
  border: 2px solid #f7931a;
  border-radius: 12px;
  padding: 12px 24px;
  color: #f7931a;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  z-index: 100;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(247, 147, 26, 0.3);
    box-shadow: 0 0 30px rgba(247, 147, 26, 0.4);
  }
`;

// ============================================================================
// Constants
// ============================================================================

// Pin Art Grid Settings
const GRID_COLS = 120;  // Horizontal pins
const GRID_ROWS = 70;   // Vertical pins  
const PARTICLE_COUNT = GRID_COLS * GRID_ROWS; // ~8400 pins
const LERP_FACTOR = 0.06;
const FRICTION = 0.94;

// Spring physics constants for wave propagation
const SPRING_STIFFNESS = 0.25;   // How quickly pins spring back (higher = faster return)
const SPRING_DAMPING = 0.85;     // Friction on Z movement (lower = faster settle)
const NEIGHBOR_INFLUENCE = 0.04; // How much neighbors affect each other

// ============================================================================
// Main Component
// ============================================================================

export const PretextChart: React.FC<PretextChartProps> = ({
  contestEntries,
  priceData = [],
  currentPrice,
  currentUserHandle,
  theme,
  width,
  height,
  onEntryHover,
  onEntryClick
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  
  const stateRef = useRef<AnimationState>({
    entryPositions: new Map(),
    particles: [],
    time: 0,
    hoveredEntry: null,
    mouseX: 0,
    mouseY: 0,
    view: {
      zoom: 1, panX: 0, panY: 0,
      targetZoom: 1, targetPanX: 0, targetPanY: 0,
      velocityX: 0, velocityY: 0,
      isDragging: false, lastMouseX: 0, lastMouseY: 0
    },
    waveTime: 0,
    waveOriginX: 400,
    waveOriginY: 300,
    displayText: '',
    textPixels: new Set()
  });
  
  const textCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  const [hoverCard, setHoverCard] = useState<{
    visible: boolean; x: number; y: number; entry: ContestEntry | null;
  }>({ visible: false, x: 0, y: 0, entry: null });
  
  const [perfStats, setPerfStats] = useState({ fps: 0, particles: PARTICLE_COUNT, zoom: 1 });
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTime = useRef(performance.now());
  
  // Text display state
  const [textInput, setTextInput] = useState({ visible: false, x: 0, y: 0 });
  const [displayText, setDisplayText] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);
  
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const margin = { top: 50, right: 100, bottom: 60, left: 80 };
  
  // ============================================================================
  // Price calculations
  // ============================================================================
  
  const { minPrice, maxPrice, avgPrice, leader } = useMemo((): {
    minPrice: number; maxPrice: number; avgPrice: number; leader: ContestEntry | null;
  } => {
    const prices = contestEntries
      .map(e => e.price_prediction)
      .filter(p => p > 1000 && p < 1000000);
    
    if (prices.length === 0) {
      return { minPrice: 50000, maxPrice: 150000, avgPrice: 100000, leader: null };
    }
    
    const min = Math.min(...prices, currentPrice > 0 ? currentPrice * 0.8 : Infinity);
    const max = Math.max(...prices, currentPrice > 0 ? currentPrice * 1.2 : 0);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    
    let leaderEntry: ContestEntry | null = null;
    let minDistance = Infinity;
    if (currentPrice > 0) {
      contestEntries.forEach(entry => {
        const dist = Math.abs(entry.price_prediction - currentPrice);
        if (dist < minDistance && entry.price_prediction > 1000) {
          minDistance = dist;
          leaderEntry = entry;
        }
      });
    }
    
    const padding = (max - min) * 0.15;
    return { minPrice: min - padding, maxPrice: max + padding, avgPrice: avg, leader: leaderEntry };
  }, [contestEntries, currentPrice]);
  
  const contestDate = new Date('2026-04-28T23:00:00Z');
  const daysUntil = Math.ceil((contestDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  
  const priceToY = useCallback((price: number) => {
    const chartHeight = dimensions.height - margin.top - margin.bottom;
    return margin.top + chartHeight * (1 - (price - minPrice) / (maxPrice - minPrice));
  }, [dimensions.height, minPrice, maxPrice, margin]);
  
  // ============================================================================
  // Initialize PIN ART GRID - tightly packed heightfield
  // ============================================================================
  
  // Track dimensions to regenerate grid when container resizes
  const lastDimensionsRef = useRef({ width: 0, height: 0 });
  // Track price data to trigger wave on updates
  const lastPriceCountRef = useRef(0);
  
  // Trigger wave when new price data arrives
  useEffect(() => {
    if (priceData && priceData.length > lastPriceCountRef.current && lastPriceCountRef.current > 0) {
      // New price data came in - trigger wave from the right side (where new data appears)
      const state = stateRef.current;
      state.waveTime = 0.001; // Start wave
      state.waveOriginX = dimensions.width - margin.right; // Right edge where new prices appear
      state.waveOriginY = dimensions.height / 2;
    }
    lastPriceCountRef.current = priceData?.length || 0;
  }, [priceData?.length, dimensions, margin]);
  
  useEffect(() => {
    const state = stateRef.current;
    
    // Regenerate if dimensions changed significantly or first time
    const dimChanged = Math.abs(lastDimensionsRef.current.width - dimensions.width) > 10 ||
                       Math.abs(lastDimensionsRef.current.height - dimensions.height) > 10;
    
    if ((state.particles.length === 0 || dimChanged) && dimensions.width > 0) {
      lastDimensionsRef.current = { ...dimensions };
      const particles: Particle[] = [];
      
      // Calculate spacing to cover the FULL canvas with small margin
      const chartLeft = 10;
      const chartRight = dimensions.width - 10;
      const chartTop = 40; // Leave room for title
      const chartBottom = dimensions.height - 25; // Leave room for footer
      const chartWidth = chartRight - chartLeft;
      const chartHeight = chartBottom - chartTop;
      
      // Grid density - 8px spacing for higher granularity
      const cols = Math.floor(chartWidth / 8);
      const rows = Math.floor(chartHeight / 8);
      
      const spacingX = chartWidth / Math.max(1, cols - 1);
      const spacingY = chartHeight / Math.max(1, rows - 1);
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const x = chartLeft + col * spacingX;
          const y = chartTop + row * spacingY;
          
          // All pins start as dark/black - they glow orange only when displaced
          // Slight variation in base darkness for texture
          const rand = Math.random();
          let color: string;
          if (rand > 0.7) {
            color = '#1a1a1a';       // Slightly lighter black
          } else if (rand > 0.4) {
            color = '#141414';       // Dark
          } else {
            color = '#0f0f0f';       // Very dark
          }
          
          particles.push({
            x, y,
            baseX: x, 
            baseY: y,
            z: 0,
            vz: 0,
            size: 2.0,  // Smaller for denser grid
            color,
            alpha: 0.65,
            row,
            col
          });
        }
      }
      state.particles = particles;
      
      // Update constants for neighbor calculations
      (window as any).__GRID_COLS = cols;
      (window as any).__GRID_ROWS = rows;
    }
  }, [dimensions, margin]);
  
  // Render text to offscreen canvas and extract pixel positions
  const renderTextToPixels = useCallback((text: string) => {
    const state = stateRef.current;
    state.textPixels.clear();
    
    if (!text) {
      state.displayText = '';
      return;
    }
    
    // Create offscreen canvas if needed
    if (!textCanvasRef.current) {
      textCanvasRef.current = document.createElement('canvas');
    }
    
    // Use the same coordinates as particle grid (chartLeft=10, chartTop=40)
    const chartLeft = 10;
    const chartTop = 40;
    const chartRight = dimensions.width - 10;
    const chartBottom = dimensions.height - 25;
    const chartWidth = chartRight - chartLeft;
    const chartHeight = chartBottom - chartTop;
    
    const canvas = textCanvasRef.current;
    canvas.width = chartWidth;
    canvas.height = chartHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear and draw text
    ctx.clearRect(0, 0, chartWidth, chartHeight);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 80px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text.toUpperCase(), chartWidth / 2, chartHeight / 2);
    
    // Sample pixels at grid intervals (8px to match particle grid)
    const gridSpacing = 8;
    const imageData = ctx.getImageData(0, 0, chartWidth, chartHeight);
    
    for (let y = 0; y < chartHeight; y += gridSpacing) {
      for (let x = 0; x < chartWidth; x += gridSpacing) {
        const idx = (y * chartWidth + x) * 4;
        const alpha = imageData.data[idx + 3];
        if (alpha > 128) {
          // Map to particle grid coordinates
          const gridX = chartLeft + x;
          const gridY = chartTop + y;
          state.textPixels.add(`${Math.round(gridX)},${Math.round(gridY)}`);
        }
      }
    }
    
    state.displayText = text;
    console.log('Text pixels:', state.textPixels.size, 'for text:', text);
  }, [dimensions]);
  
  // ============================================================================
  // Drawing functions
  // ============================================================================
  
  const drawBackground = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, '#05050a');
    gradient.addColorStop(0.5, '#0a0a15');
    gradient.addColorStop(1, '#050510');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  }, []);
  
  const drawParticles = useCallback((ctx: CanvasRenderingContext2D, mouseX: number, mouseY: number) => {
    const state = stateRef.current;
    const particles = state.particles;
    if (particles.length === 0) return;
    
    // Get dynamic grid dimensions
    const gridCols = (window as any).__GRID_COLS || Math.sqrt(particles.length);
    const gridRows = (window as any).__GRID_ROWS || Math.sqrt(particles.length);
    
    // Wave parameters - fine, slow ripple effect
    const waveActive = state.waveTime > 0 && state.waveTime < 8;
    const waveSpeed = 80; // Slow spread
    const waveRadius = state.waveTime * waveSpeed;
    const waveWidth = 40; // Narrow ripple band for fine effect
    const waveStrength = Math.max(0, 1 - state.waveTime / 8) * 8; // Gentle strength
    
    // Mouse influence radius - smaller for precise interaction
    const mouseRadius = 60;
    const mouseRadiusSq = mouseRadius * mouseRadius;
    
    // Transform mouse coordinates to match chart transform
    const { zoom, panX, panY } = state.view;
    const transformedMouseX = (mouseX - dimensions.width / 2 - panX) / zoom + dimensions.width / 2;
    const transformedMouseY = (mouseY - dimensions.height / 2 - panY) / zoom + dimensions.height / 2;
    
    // First pass: Update physics for all pins
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      
      // Wave displacement - only if wave is active
      if (waveActive) {
        const waveDx = p.baseX - state.waveOriginX;
        const waveDy = p.baseY - state.waveOriginY;
        const waveDistSq = waveDx * waveDx + waveDy * waveDy;
        const waveDist = Math.sqrt(waveDistSq);
        const distFromWaveFront = Math.abs(waveDist - waveRadius);
        
        if (distFromWaveFront < waveWidth) {
          const wavePhase = (distFromWaveFront / waveWidth) * Math.PI;
          const displacement = Math.sin(wavePhase) * waveStrength;
          p.vz += displacement * 0.15; // Gentle wave push
        }
      }
      
      // Mouse proximity - use squared distance (no sqrt!)
      const mouseDx = p.baseX - transformedMouseX;
      const mouseDy = p.baseY - transformedMouseY;
      const mouseDistSq = mouseDx * mouseDx + mouseDy * mouseDy;
      
      if (mouseDistSq < mouseRadiusSq) {
        // Approximate influence without sqrt - stronger for visibility
        const influence = 1 - mouseDistSq / mouseRadiusSq;
        p.vz += influence * 0.8;
      }
      
      // Text display - raise pins that form letters
      if (state.textPixels.size > 0) {
        const key = `${Math.round(p.baseX)},${Math.round(p.baseY)}`;
        // Check nearby positions too for smoother text
        const isTextPixel = state.textPixels.has(key) ||
          state.textPixels.has(`${Math.round(p.baseX) + 8},${Math.round(p.baseY)}`) ||
          state.textPixels.has(`${Math.round(p.baseX) - 8},${Math.round(p.baseY)}`) ||
          state.textPixels.has(`${Math.round(p.baseX)},${Math.round(p.baseY) + 8}`) ||
          state.textPixels.has(`${Math.round(p.baseX)},${Math.round(p.baseY) - 8}`);
        
        if (isTextPixel) {
          // Gently push up pins that form text
          const targetZ = 4;
          p.vz += (targetZ - p.z) * 0.1;
        }
      }
      
      // Spring physics: pull back to z=0
      p.vz -= p.z * SPRING_STIFFNESS;
      
      // Apply damping
      p.vz *= SPRING_DAMPING;
      
      // Update position
      p.z += p.vz;
      
      // Clamp Z to prevent explosion
      p.z = Math.max(-10, Math.min(15, p.z));
    }
    
    // Second pass: Neighbor influence (wave propagation)
    // This creates the fabric-like connected feel
    for (let row = 1; row < gridRows - 1; row++) {
      for (let col = 1; col < gridCols - 1; col++) {
        const idx = row * gridCols + col;
        if (idx >= particles.length) continue;
        const p = particles[idx];
        
        // Get neighbors
        const topIdx = (row - 1) * gridCols + col;
        const bottomIdx = (row + 1) * gridCols + col;
        const leftIdx = row * gridCols + (col - 1);
        const rightIdx = row * gridCols + (col + 1);
        
        if (topIdx >= 0 && bottomIdx < particles.length && 
            leftIdx >= 0 && rightIdx < particles.length) {
          const top = particles[topIdx];
          const bottom = particles[bottomIdx];
          const left = particles[leftIdx];
          const right = particles[rightIdx];
          
          // Average neighbor height influences this pin
          const avgNeighborZ = (top.z + bottom.z + left.z + right.z) / 4;
          p.vz += (avgNeighborZ - p.z) * NEIGHBOR_INFLUENCE;
        }
      }
    }
    
    // Third pass: Draw all pins - dark at rest, orange when displaced
    // Group particles by color bucket for batched drawing
    const colorBuckets: Map<string, Array<{x: number, y: number, size: number}>> = new Map();
    
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      
      // Height affects visual properties
      const absZ = Math.abs(p.z);
      const displaySize = p.size + absZ * 0.4; // Bigger size change when displaced
      const displayY = p.baseY - p.z * 0.8; // More prominent lift
      
      // Color based on displacement - dark at rest, bright orange when moving
      let color: string;
      if (absZ > 2) {
        // Bright orange - highly displaced
        color = 'rgba(255,165,0,1)';
      } else if (absZ > 1) {
        // Medium orange glow
        const intensity = (absZ - 1) / 1; // 0-1
        const r = Math.round(60 + 195 * intensity);
        const g = Math.round(40 + 125 * intensity);
        const b = Math.round(20 * intensity);
        color = `rgb(${r},${g},${b})`;
      } else if (absZ > 0.3) {
        // Subtle warm glow starting
        const intensity = (absZ - 0.3) / 0.7; // 0-1
        const r = Math.round(30 + 30 * intensity);
        const g = Math.round(25 + 15 * intensity);
        const b = Math.round(20 * intensity);
        color = `rgb(${r},${g},${b})`;
      } else {
        // At rest - slightly visible dark grey
        color = '#1a1a1a';
      }
      
      if (!colorBuckets.has(color)) {
        colorBuckets.set(color, []);
      }
      colorBuckets.get(color)!.push({ x: p.baseX, y: displayY, size: displaySize });
    }
    
    // Draw all particles of same color in one batch
    colorBuckets.forEach((pins, color) => {
      ctx.fillStyle = color;
      for (let j = 0; j < pins.length; j++) {
        const pin = pins[j];
        // Use fillRect instead of arc - MUCH faster
        const half = pin.size;
        ctx.fillRect(pin.x - half, pin.y - half, half * 2, half * 2);
      }
    });
  }, []);
  
  const drawYAxis = useCallback((ctx: CanvasRenderingContext2D) => {
    const chartHeight = dimensions.height - margin.top - margin.bottom;
    const tickCount = 6;
    
    for (let i = 0; i <= tickCount; i++) {
      const y = margin.top + (chartHeight / tickCount) * i;
      const price = maxPrice - (i / tickCount) * (maxPrice - minPrice);
      
      ctx.strokeStyle = 'rgba(247, 147, 26, 0.06)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 8]);
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(dimensions.width - margin.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      
      const label = `$${Math.round(price).toLocaleString()}`;
      ctx.font = '11px Inter, system-ui';
      ctx.fillStyle = '#555';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, margin.left - 10, y);
    }
  }, [dimensions, margin, minPrice, maxPrice]);
  
  const drawPriceLine = useCallback((ctx: CanvasRenderingContext2D) => {
    if (!priceData || priceData.length < 2) {
      // Draw placeholder message if no price data
      ctx.font = '12px Inter';
      ctx.fillStyle = '#666';
      ctx.textAlign = 'center';
      ctx.fillText('Loading price data...', dimensions.width / 2, dimensions.height / 2 - 50);
      return;
    }
    
    const chartWidth = dimensions.width - margin.left - margin.right;
    const times = priceData.map(d => d.date.getTime());
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const timeRange = maxTime - minTime || 1;
    
    // Draw glow effect first (thicker, semi-transparent)
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(247, 147, 26, 0.3)';
    ctx.lineWidth = 8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    priceData.forEach((point, i) => {
      const x = margin.left + ((point.date.getTime() - minTime) / timeRange) * chartWidth * 0.65;
      const y = priceToY(point.close);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    
    // Draw main line (bright, thicker)
    ctx.beginPath();
    ctx.strokeStyle = '#f7931a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    priceData.forEach((point, i) => {
      const x = margin.left + ((point.date.getTime() - minTime) / timeRange) * chartWidth * 0.65;
      const y = priceToY(point.close);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    
    ctx.stroke();
    
    // Gradient fill under the line
    ctx.beginPath();
    priceData.forEach((point, i) => {
      const x = margin.left + ((point.date.getTime() - minTime) / timeRange) * chartWidth * 0.65;
      const y = priceToY(point.close);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    
    const gradient = ctx.createLinearGradient(0, margin.top, 0, dimensions.height - margin.bottom);
    gradient.addColorStop(0, 'rgba(247, 147, 26, 0.15)');
    gradient.addColorStop(1, 'rgba(247, 147, 26, 0)');
    
    ctx.lineTo(margin.left + chartWidth * 0.65, dimensions.height - margin.bottom);
    ctx.lineTo(margin.left, dimensions.height - margin.bottom);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();
  }, [priceData, dimensions, margin, priceToY]);
  
  const drawContestLine = useCallback((ctx: CanvasRenderingContext2D): number => {
    const chartWidth = dimensions.width - margin.left - margin.right;
    const xPos = margin.left + chartWidth * 0.75;
    
    const gradient = ctx.createLinearGradient(xPos, margin.top, xPos, dimensions.height - margin.bottom);
    gradient.addColorStop(0, 'rgba(247, 147, 26, 0.02)');
    gradient.addColorStop(0.3, 'rgba(247, 147, 26, 0.5)');
    gradient.addColorStop(0.7, 'rgba(247, 147, 26, 0.5)');
    gradient.addColorStop(1, 'rgba(247, 147, 26, 0.02)');
    
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(xPos, margin.top);
    ctx.lineTo(xPos, dimensions.height - margin.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Contest label
    const labelW = 150, labelH = 48;
    ctx.fillStyle = 'rgba(10, 10, 20, 0.9)';
    ctx.strokeStyle = theme.primary;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(xPos - labelW/2, margin.top + 5, labelW, labelH, 8);
    ctx.fill();
    ctx.stroke();
    
    ctx.font = 'bold 12px Inter';
    ctx.fillStyle = theme.primary;
    ctx.textAlign = 'center';
    ctx.fillText('🎯 CONTEST DATE', xPos, margin.top + 24);
    ctx.font = '11px Inter';
    ctx.fillStyle = '#fff';
    ctx.fillText('April 28, 2026 • 4PM PT', xPos, margin.top + 42);
    
    // Countdown
    ctx.fillStyle = theme.primary;
    ctx.beginPath();
    ctx.roundRect(xPos - 40, dimensions.height - margin.bottom - 35, 80, 24, 12);
    ctx.fill();
    ctx.font = 'bold 11px Inter';
    ctx.fillStyle = '#000';
    ctx.fillText(`${daysUntil}d left`, xPos, dimensions.height - margin.bottom - 23);
    
    return xPos;
  }, [dimensions, margin, theme, daysUntil]);
  
  const drawCurrentPriceLine = useCallback((ctx: CanvasRenderingContext2D) => {
    if (currentPrice <= 0) return;
    
    const y = priceToY(currentPrice);
    if (y < margin.top || y > dimensions.height - margin.bottom) return;
    
    const time = stateRef.current.time;
    const pulse = Math.sin(time * 2) * 0.2 + 0.8;
    
    ctx.strokeStyle = `rgba(0, 211, 149, ${pulse})`;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(dimensions.width - margin.right, y);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Price label
    ctx.fillStyle = '#00d395';
    ctx.beginPath();
    ctx.roundRect(dimensions.width - margin.right + 5, y - 14, 85, 28, 4);
    ctx.fill();
    
    ctx.font = 'bold 12px Inter';
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.fillText(`$${currentPrice.toLocaleString()}`, dimensions.width - margin.right + 47, y + 1);
    
    ctx.font = '9px Inter';
    ctx.fillStyle = '#00d395';
    ctx.textAlign = 'left';
    ctx.fillText('● LIVE', dimensions.width - margin.right + 8, y - 20);
  }, [currentPrice, priceToY, dimensions, margin]);
  
  // ============================================================================
  // DYNAMIC STARBURST - Spreads out on zoom!
  // ============================================================================
  
  const drawDynamicStarburst = useCallback((ctx: CanvasRenderingContext2D, contestX: number, time: number) => {
    const state = stateRef.current;
    const { zoom } = state.view;
    
    // KEY: Spread increases dramatically with zoom
    const baseSpread = 20;
    const zoomSpread = baseSpread * zoom * 1.5; // Much more spread when zoomed
    
    // Group entries by price proximity
    const visiblePriceRange = (maxPrice - minPrice) / zoom;
    const groupThreshold = visiblePriceRange * 0.02;
    
    const sortedEntries = [...contestEntries].sort((a, b) => 
      a.price_prediction - b.price_prediction
    );
    
    const priceGroups: Map<number, ContestEntry[]> = new Map();
    
    sortedEntries.forEach(entry => {
      const price = entry.price_prediction;
      if (price < minPrice || price > maxPrice) return;
      
      let foundGroup = false;
      const groupEntries = Array.from(priceGroups.entries());
      for (const [groupPrice, group] of groupEntries) {
        if (Math.abs(price - groupPrice) < groupThreshold) {
          group.push(entry);
          foundGroup = true;
          break;
        }
      }
      if (!foundGroup) {
        priceGroups.set(price, [entry]);
      }
    });
    
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    let globalIndex = 0;
    
    priceGroups.forEach((groupEntries, groupPrice) => {
      groupEntries.forEach((entry, indexInGroup) => {
        const baseY = priceToY(entry.price_prediction);
        
        let xOffset = 0;
        let yOffset = 0;
        
        if (indexInGroup > 0 || groupEntries.length > 1) {
          const ringIndex = Math.floor(indexInGroup / 6);
          const ringRadius = zoomSpread + ringIndex * (zoomSpread * 0.5);
          const angle = globalIndex * goldenAngle;
          const seed = entry.id || globalIndex;
          const jitter = ((seed * 9301 + 49297) % 233280) / 233280 * 0.15 - 0.075;
          
          xOffset = Math.cos(angle + jitter) * ringRadius;
          yOffset = Math.sin(angle + jitter) * ringRadius * 0.4;
        }
        
        const targetX = contestX + xOffset;
        const targetY = baseY + yOffset;
        
        let pos = state.entryPositions.get(entry.id);
        if (!pos) {
          pos = { x: contestX, y: baseY, targetX, targetY, baseY };
          state.entryPositions.set(entry.id, pos);
        }
        
        pos.targetX = targetX;
        pos.targetY = targetY;
        pos.baseY = baseY;
        
        // SMOOTH interpolation
        pos.x += (pos.targetX - pos.x) * LERP_FACTOR;
        pos.y += (pos.targetY - pos.y) * LERP_FACTOR;
        
        // Connection ray
        if (Math.abs(xOffset) > 5) {
          ctx.strokeStyle = `rgba(247, 147, 26, ${0.06 + Math.sin(time + globalIndex) * 0.03})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(contestX, baseY);
          ctx.lineTo(pos.x, pos.y);
          ctx.stroke();
        }
        
        // Draw entry
        const isLeader = entry.id === leader?.id;
        const isCurrentUser = entry.twitter_handle === currentUserHandle;
        const isHovered = state.hoveredEntry === entry.id;
        
        drawEntryDot(ctx, entry, pos.x, pos.y, isLeader, isCurrentUser, isHovered, time);
        
        globalIndex++;
      });
    });
  }, [contestEntries, leader, currentUserHandle, minPrice, maxPrice, priceToY]);
  
  const drawEntryDot = useCallback((
    ctx: CanvasRenderingContext2D,
    entry: ContestEntry,
    x: number, y: number,
    isLeader: boolean,
    isCurrentUser: boolean,
    isHovered: boolean,
    time: number
  ) => {
    const { zoom } = stateRef.current.view;
    
    const entryCount = contestEntries.length;
    const baseRadius = entryCount > 50 ? 4 : entryCount > 30 ? 5 : 6;
    const zoomRadius = baseRadius * Math.min(1.8, Math.max(0.6, zoom * 0.7));
    const hoverRadius = isHovered ? zoomRadius * 1.6 : zoomRadius;
    const pulseRadius = isLeader ? hoverRadius * (1 + Math.sin(time * 3) * 0.15) : hoverRadius;
    
    // Glow
    if (isLeader || isHovered) {
      const glowColor = isLeader ? 'rgba(255, 215, 0, 0.5)' : 'rgba(247, 147, 26, 0.4)';
      const glowRadius = pulseRadius * 3;
      
      const gradient = ctx.createRadialGradient(x, y, pulseRadius * 0.3, x, y, glowRadius);
      gradient.addColorStop(0, glowColor);
      gradient.addColorStop(1, 'transparent');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Dot color - Production orange/grey theme
    let dotColor = theme.primary;
    if (isLeader) dotColor = '#FFD700';           // Gold for leader
    else if (isCurrentUser) dotColor = '#f7931a'; // Bright orange for current user
    else if (entry.price_prediction > (currentPrice || 75000)) dotColor = '#d4740f'; // Deep orange (bullish)
    else dotColor = '#8b5a00';                    // Brown orange (bearish)
    
    // Main dot
    ctx.fillStyle = dotColor;
    ctx.beginPath();
    ctx.arc(x, y, pulseRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.beginPath();
    ctx.arc(x, y, pulseRadius * 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Leader crown
    if (isLeader) {
      ctx.font = '14px Inter';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('👑', x, y - pulseRadius - 4);
    }
    
    // HOVER LABEL - Always show prominently when hovered
    if (isHovered) {
      const labelY = y - pulseRadius - 15;
      const handle = `@${entry.twitter_handle}`;
      const price = `$${entry.price_prediction.toLocaleString()}`;
      
      // Measure text for proper sizing
      ctx.font = 'bold 12px Inter';
      const handleWidth = ctx.measureText(handle).width;
      ctx.font = 'bold 16px Inter';
      const priceWidth = ctx.measureText(price).width;
      const labelW = Math.max(handleWidth, priceWidth) + 30;
      const labelH = 50;
      
      // Background
      ctx.fillStyle = 'rgba(5, 5, 15, 0.95)';
      ctx.strokeStyle = dotColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x - labelW/2, labelY - labelH, labelW, labelH, 8);
      ctx.fill();
      ctx.stroke();
      
      // Handle
      ctx.font = 'bold 12px Inter';
      ctx.fillStyle = '#f7931a'; // Bitcoin orange for handle
      ctx.textAlign = 'center';
      ctx.fillText(handle, x, labelY - 32);
      
      // Price
      ctx.font = 'bold 16px Inter';
      ctx.fillStyle = dotColor;
      ctx.fillText(price, x, labelY - 12);
    }
  }, [contestEntries.length, currentPrice, theme]);
  
  const drawStatsPanel = useCallback((ctx: CanvasRenderingContext2D) => {
    const panelX = margin.left + 10;
    const panelY = margin.top + 70;
    const panelW = 160;
    const panelH = 75;
    
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, 8);
    ctx.fill();
    
    ctx.font = 'bold 26px Inter';
    ctx.fillStyle = theme.primary;
    ctx.textAlign = 'left';
    ctx.fillText(`${contestEntries.length}`, panelX + 12, panelY + 32);
    
    ctx.font = '11px Inter';
    ctx.fillStyle = '#666';
    ctx.fillText('entries', panelX + 65, panelY + 28);
    
    ctx.font = 'bold 13px Inter';
    ctx.fillStyle = '#fff';
    ctx.fillText(`$${Math.round(avgPrice).toLocaleString()}`, panelX + 12, panelY + 52);
    
    ctx.font = '9px Inter';
    ctx.fillStyle = '#555';
    ctx.fillText('avg prediction', panelX + 12, panelY + 66);
    
    if (leader) {
      ctx.fillStyle = '#FFD700';
      ctx.fillText(`👑 @${leader.twitter_handle}`, panelX + 85, panelY + 66);
    }
  }, [contestEntries.length, avgPrice, leader, margin, theme]);
  
  // ============================================================================
  // View state update
  // ============================================================================
  
  const updateViewState = useCallback(() => {
    const view = stateRef.current.view;
    
    if (!view.isDragging) {
      view.targetPanX += view.velocityX;
      view.targetPanY += view.velocityY;
      view.velocityX *= FRICTION;
      view.velocityY *= FRICTION;
      
      if (Math.abs(view.velocityX) < 0.05) view.velocityX = 0;
      if (Math.abs(view.velocityY) < 0.05) view.velocityY = 0;
    }
    
    view.zoom += (view.targetZoom - view.zoom) * LERP_FACTOR;
    view.panX += (view.targetPanX - view.panX) * LERP_FACTOR;
    view.panY += (view.targetPanY - view.panY) * LERP_FACTOR;
    
    view.zoom = Math.max(0.3, Math.min(6, view.zoom));
    view.targetZoom = Math.max(0.3, Math.min(6, view.targetZoom));
  }, []);
  
  // ============================================================================
  // Main render loop
  // ============================================================================
  
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    const startTime = performance.now();
    const state = stateRef.current;
    state.time += 0.016;
    
    // Update wave time (advances the ripple)
    if (state.waveTime > 0) {
      state.waveTime += 0.016;
      // Reset wave after it finishes (7 seconds for smooth settle)
      if (state.waveTime > 7) {
        state.waveTime = 0;
      }
    }
    
    updateViewState();
    const { zoom, panX, panY } = state.view;
    const { width: w, height: h } = dimensions;
    
    drawBackground(ctx, w, h);
    
    // Draw particles WITHOUT transform - pins stay fixed on canvas
    drawParticles(ctx, state.mouseX, state.mouseY);
    
    // Only apply transform to chart elements (price line, etc) - disabled for pin art mode
    // ctx.save();
    // ctx.translate(w / 2 + panX, h / 2 + panY);
    // ctx.scale(zoom, zoom);
    // ctx.translate(-w / 2, -h / 2);
    ctx.save();
    
    drawYAxis(ctx);
    drawPriceLine(ctx);
    const contestX = drawContestLine(ctx);
    drawCurrentPriceLine(ctx);
    drawDynamicStarburst(ctx, contestX, state.time);
    
    ctx.restore();
    
    drawStatsPanel(ctx);
    
    ctx.font = 'bold 18px Inter';
    ctx.fillStyle = theme.primary;
    ctx.textAlign = 'center';
    ctx.fillText('₿ Bitcoin Conference 2026 Contest', w / 2, 28);
    
    ctx.font = '10px Inter';
    ctx.fillStyle = '#444';
    ctx.fillText(`${zoom.toFixed(2)}× • Scroll: zoom • Drag: pan • Double-click: reset`, w / 2, h - 12);
    
    // Performance
    const renderTime = performance.now() - startTime;
    const now = performance.now();
    frameTimesRef.current.push(now - lastFrameTime.current);
    lastFrameTime.current = now;
    
    if (frameTimesRef.current.length > 60) frameTimesRef.current.shift();
    
    if (frameTimesRef.current.length >= 30) {
      const avgFrameTime = frameTimesRef.current.reduce((a, b) => a + b, 0) / frameTimesRef.current.length;
      const fps = Math.round(1000 / avgFrameTime);
      setPerfStats({ fps, particles: PARTICLE_COUNT, zoom });
    }
    
    animationRef.current = requestAnimationFrame(render);
  }, [dimensions, drawBackground, drawParticles, drawYAxis, drawPriceLine, 
      drawContestLine, drawCurrentPriceLine, drawDynamicStarburst, drawStatsPanel, 
      theme, updateViewState]);
  
  // ============================================================================
  // Event handlers
  // ============================================================================
  
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Disable zoom - pins should stay fixed
    // Just trigger a small wave instead
    const state = stateRef.current;
    const canvas = canvasRef.current;
    if (canvas && state.waveTime === 0) {
      const rect = canvas.getBoundingClientRect();
      state.waveOriginX = (e.clientX - rect.left) / rect.width * dimensions.width;
      state.waveOriginY = (e.clientY - rect.top) / rect.height * dimensions.height;
      state.waveTime = 0.001;
    }
  }, [dimensions]);
  
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't enable dragging/panning - pins should stay fixed
    // Instead, start continuous wave effect
    const state = stateRef.current;
    state.view.isDragging = true;
    state.view.lastMouseX = e.clientX;
    state.view.lastMouseY = e.clientY;
  }, []);
  
  const handleMouseUp = useCallback(() => {
    stateRef.current.view.isDragging = false;
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    stateRef.current.view.isDragging = false;
    setHoverCard(prev => ({ ...prev, visible: false }));
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const state = stateRef.current;
    const view = state.view;
    const rect = canvas.getBoundingClientRect();
    
    state.mouseX = (e.clientX - rect.left) / rect.width * dimensions.width;
    state.mouseY = (e.clientY - rect.top) / rect.height * dimensions.height;
    
    if (view.isDragging) {
      // When dragging, just update mouse position for continuous interaction
      // Don't pan the canvas
      return;
    }
    
    // Hit testing
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    const { zoom, panX, panY } = view;
    const transformedX = (x - dimensions.width / 2 - panX) / zoom + dimensions.width / 2;
    const transformedY = (y - dimensions.height / 2 - panY) / zoom + dimensions.height / 2;
    
    let hoveredId: number | null = null;
    let hoveredEntry: ContestEntry | null = null;
    let minDist = 20 / zoom;
    
    state.entryPositions.forEach((pos, id) => {
      const dist = Math.sqrt(Math.pow(transformedX - pos.x, 2) + Math.pow(transformedY - pos.y, 2));
      if (dist < minDist) {
        minDist = dist;
        hoveredId = id;
        hoveredEntry = contestEntries.find(e => e.id === id) || null;
      }
    });
    
    state.hoveredEntry = hoveredId;
    
    if (hoveredEntry) {
      setHoverCard({
        visible: true,
        x: e.clientX - (containerRef.current?.getBoundingClientRect().left || 0),
        y: e.clientY - (containerRef.current?.getBoundingClientRect().top || 0),
        entry: hoveredEntry
      });
      onEntryHover?.(hoveredEntry);
    } else {
      setHoverCard(prev => ({ ...prev, visible: false }));
      onEntryHover?.(null);
    }
  }, [contestEntries, dimensions, onEntryHover]);
  
  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Show text input at double-click position
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setTextInput({ visible: true, x, y });
      // Focus the input after a short delay
      setTimeout(() => textInputRef.current?.focus(), 50);
    }
  }, []);
  
  const handleTextButtonClick = useCallback(() => {
    // Show text input in center of chart
    setTextInput({ visible: true, x: dimensions.width / 2, y: dimensions.height / 2 });
    setTimeout(() => textInputRef.current?.focus(), 50);
  }, [dimensions]);
  
  const handleTextSubmit = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = (e.target as HTMLInputElement).value.trim();
      if (value) {
        // Render text to pin grid
        renderTextToPixels(value);
        setDisplayText(value);
        // Trigger wave from center when text is set
        const state = stateRef.current;
        state.waveOriginX = dimensions.width / 2;
        state.waveOriginY = dimensions.height / 2;
        state.waveTime = 0.001;
      } else {
        // Clear text
        renderTextToPixels('');
        setDisplayText('');
      }
      setTextInput({ visible: false, x: 0, y: 0 });
      (e.target as HTMLInputElement).value = '';
    } else if (e.key === 'Escape') {
      setTextInput({ visible: false, x: 0, y: 0 });
      (e.target as HTMLInputElement).value = '';
    }
  }, [renderTextToPixels, dimensions]);
  
  // Clear text on triple click
  const handleTripleClick = useCallback(() => {
    renderTextToPixels('');
    setDisplayText('');
  }, [renderTextToPixels]);
  
  // Click triggers wave from cursor position (and handles entry clicks)
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const state = stateRef.current;
    
    // Trigger wave from click position
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // Transform click coordinates to match chart transform
      const { zoom, panX, panY } = state.view;
      state.waveOriginX = (clickX - dimensions.width / 2 - panX) / zoom + dimensions.width / 2;
      state.waveOriginY = (clickY - dimensions.height / 2 - panY) / zoom + dimensions.height / 2;
      state.waveTime = 0.001; // Start wave
    }
    
    // Also handle entry clicks
    if (state.hoveredEntry) {
      const entry = contestEntries.find(e => e.id === state.hoveredEntry);
      if (entry) {
        console.log('Clicked:', entry.twitter_handle, '$' + entry.price_prediction.toLocaleString());
        onEntryClick?.(entry);
      }
    }
  }, [contestEntries, onEntryClick, dimensions]);
  
  // ============================================================================
  // Effects
  // ============================================================================
  
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({ 
          width: width || clientWidth || 800, 
          height: height || clientHeight || 500 
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    
    render();
    
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [dimensions, render]);
  
  // Wheel event with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);
  
  // Demo: Trigger wave ripple every 10 seconds (simulating price updates)
  useEffect(() => {
    const triggerPriceWave = () => {
      const state = stateRef.current;
      // Force start a new wave from the right edge (where live price appears)
      state.waveOriginX = dimensions.width - margin.right - 20;
      state.waveOriginY = dimensions.height / 2;
      state.waveTime = 0.001; // Start the wave
      console.log('Wave triggered! Origin:', state.waveOriginX, state.waveOriginY);
    };
    
    // Trigger initial wave after 2 seconds
    const initialTimeout = setTimeout(triggerPriceWave, 2000);
    
    // Then trigger every 10 seconds (demo price update simulation)
    const interval = setInterval(triggerPriceWave, 10000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [dimensions, margin]);
  
  // Also trigger wave on actual price updates
  const lastPriceRef = useRef(currentPrice);
  useEffect(() => {
    if (currentPrice > 0 && Math.abs(currentPrice - lastPriceRef.current) > 10) {
      lastPriceRef.current = currentPrice;
      const state = stateRef.current;
      // Trigger wave from right edge on price update
      state.waveOriginX = dimensions.width - margin.right - 20;
      state.waveOriginY = dimensions.height / 2;
      state.waveTime = 0.001;
    }
  }, [currentPrice, dimensions, margin]);
  
  // ============================================================================
  // Render
  // ============================================================================
  
  return (
    <ChartContainer ref={containerRef} theme={theme}>
      <Canvas 
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
      
      <HoverCard $visible={hoverCard.visible} $x={hoverCard.x} $y={hoverCard.y}>
        {hoverCard.entry && (
          <>
            <HoverHandle>@{hoverCard.entry.twitter_handle}</HoverHandle>
            <HoverPrice>${hoverCard.entry.price_prediction.toLocaleString()}</HoverPrice>
            <HoverMeta>
              {hoverCard.entry.display_name && <div>{hoverCard.entry.display_name}</div>}
              <div>{new Date(hoverCard.entry.submitted_at).toLocaleDateString()}</div>
            </HoverMeta>
          </>
        )}
      </HoverCard>
      
      <PerformanceStats>
        {perfStats.fps} FPS | {perfStats.particles} particles | {perfStats.zoom.toFixed(2)}×
      </PerformanceStats>
      
      {!textInput.visible && (
        <TextInputButton onClick={handleTextButtonClick}>
          ✨ Type Text to Display
        </TextInputButton>
      )}
      
      <TextInputOverlay $visible={textInput.visible} $x={textInput.x} $y={textInput.y}>
        <TextInput
          ref={textInputRef}
          placeholder="Type text..."
          onKeyDown={handleTextSubmit}
          onBlur={() => setTextInput({ visible: false, x: 0, y: 0 })}
        />
      </TextInputOverlay>
    </ChartContainer>
  );
};

export default PretextChart;
